import { bool, func, object } from 'prop-types';
import {
  ModalStyled,
  ModalActionsStyled,
  TextStyled,
  ButtonStyled,
  IconButton,
  useTokens
} from '@nike/nike-design-system-components';
import { Plus, Delete } from '@nike/nike-design-system-icons';

import { Formik, FieldArray } from 'formik';

import LineItemAttribute from './LineItemAttribute';
import ProductImage from '../../../components/aurora/ProductImage';
import Spinner from '../../../components/spinner/Spinner';

const EditLineItemModal = ({ isOpen, closeModal, onConfirm, item, requestInProgress }) => {
  const tokens = useTokens();

  return (
    <ModalStyled
      isOpen={isOpen}
      closeModal={closeModal}
      title='Edit Line Item Sizes'
      appearance='base'
      shouldCloseOnBackgroundClick={false}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ paddingRight: '10px' }}>
          <LineItemAttribute attributeName='Product Code' value={item.productCode} />
          <LineItemAttribute attributeName='Product Name' value={item.productName} />
          <LineItemAttribute attributeName='Color' value={item.colorName} />
        </div>
        <div>
          <ProductImage productCode={item.productCode} alt={item.productName} width={100} height={100} />
        </div>
      </div>
      <Formik initialValues={{ sizes: item.sizes }} onSubmit={({ sizes }) => onConfirm(sizes)}>
        {({ values, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FieldArray name='sizes'>
              {({ push, remove }) => (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: `1px solid ${tokens.podiumCdsColorGrey300}`,
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', background: tokens.podiumCdsColorGrey100 }}>
                      <div
                        style={{
                          flex: 1,
                          borderRight: `1px solid ${tokens.podiumCdsColorGrey300}`
                        }}
                      >
                        <div style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <TextStyled appearance='body2Strong'>Size Code</TextStyled>
                        </div>
                      </div>
                      <div style={{ flex: 1, width: '25px', borderRight: `1px solid ${tokens.podiumCdsColorGrey300}` }}>
                        <div style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <TextStyled appearance='body2Strong'>Quantity</TextStyled>
                        </div>
                      </div>
                      <div style={{ flex: 1, maxWidth: '80px' }}>
                        <div style={{ padding: '10px 16px', textAlign: 'center' }}> </div>
                      </div>
                    </div>
                    {values.sizes.map((size, idx) => {
                      const isNewRow = !item.sizes[idx];
                      return (
                        <div style={{ display: 'flex', borderTop: `1px solid ${tokens.podiumCdsColorGrey300}` }}>
                          <div
                            style={{
                              flex: 1,
                              borderRight: `1px solid ${tokens.podiumCdsColorGrey300}`
                            }}
                          >
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                justifyContent: 'center',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {isNewRow ? (
                                <div style={{ padding: '10px 16px' }}>
                                  <input
                                    name={`sizes[${idx}].sizeCode`}
                                    value={values.sizes[idx].sizeCode}
                                    onChange={handleChange}
                                    style={{
                                      width: '100%',
                                      borderRadius: '6px',
                                      border: `1px solid ${tokens.podiumCdsColorGrey300}`,
                                      padding: '6px 10px'
                                    }}
                                    aria-label='Size Code'
                                  />
                                </div>
                              ) : (
                                <TextStyled appearance='body2'>{size.sizeCode}</TextStyled>
                              )}
                            </div>
                          </div>
                          <div style={{ flex: 1, borderRight: `1px solid ${tokens.podiumCdsColorGrey300}` }}>
                            <div style={{ padding: '10px 16px' }}>
                              <input
                                name={`sizes[${idx}].quantity`}
                                value={values.sizes[idx].quantity}
                                onChange={handleChange}
                                type='number'
                                style={{
                                  width: '100%',
                                  borderRadius: '6px',
                                  border: `1px solid ${tokens.podiumCdsColorGrey300}`,
                                  padding: '6px 10px'
                                }}
                                aria-label='Quantity'
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              maxWidth: '80px',
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <IconButton
                              Icon={<Delete />}
                              onClick={() => remove(idx)}
                              appearance='secondary'
                              size='small'
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px', marginBottom: '20px' }}>
                    <IconButton
                      Icon={<Plus />}
                      appearance='secondary'
                      size='small'
                      onClick={() => push({ sizeCode: '', quantity: '' })}
                    />
                  </div>
                  {requestInProgress && (
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <Spinner className='relative-container' />
                    </div>
                  )}
                  <ModalActionsStyled>
                    <ButtonStyled type='submit' appearance='primary' disabled={requestInProgress}>
                      Apply Changes
                    </ButtonStyled>
                    <ButtonStyled
                      type='button'
                      appearance='secondary'
                      onClick={closeModal}
                      disabled={requestInProgress}
                    >
                      Cancel
                    </ButtonStyled>
                  </ModalActionsStyled>
                </>
              )}
            </FieldArray>
          </form>
        )}
      </Formik>
    </ModalStyled>
  );
};

EditLineItemModal.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
  onConfirm: func.isRequired,
  item: object.isRequired,
  requestInProgress: bool.isRequired
};

export default EditLineItemModal;
